import React, { useEffect, useRef, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { adminGetUserNameApi } from '../../../api/admin/user'
import { swalAlert, swalConfirm, swalErrorAlert, swalToast } from '../../../utils/swal/swal'
import { useForm } from 'react-hook-form'
import AdminInputFileList from '../../../components/admin/common/AdminInputFileList'
import { adminCounselSchema } from '../../../utils/validation/admin/yup/adminCounselYup'
import { yupResolver } from '@hookform/resolvers/yup'
import { adminDeleteCounselApi, adminGetCounselInfoApi, adminSaveCounselApi } from '../../../api/admin/counsel'
import AdminSavedFileList from '../../../components/admin/common/AdminSavedFileList'
import { useAdminStore } from '../../../store/userStore'

const AdminCounselWrite = ({ writeType }) => {
    const { userId, counselId } = useParams()

    const navigate = useNavigate();

    const [userName, setUserName] = useState('')

    useEffect(() => {
        getUserName(userId)
    }, [])

    const getUserName = async (userId) => {
        if (!userId) {
            setUserName('')
            return
        }

        try {
            const response = await adminGetUserNameApi(userId)

            if (response.data?.code === 200) {
                const userName = response.data?.resData?.user_name
                setUserName(userName)
            } else {
                setUserName('')
            }
            return

        } catch (error) {
            setUserName('')
            swalErrorAlert()
            return
        }
    }

    const handleDeleteCounselPost = () => {
        const confirmFunc = async () => {
            try {
                await adminDeleteCounselApi(counselId)

                swalAlert('삭제 완료', '상담내용을 삭제하였습니다.', 'success', '확인',
                    () => { navigate(`/hy-admin/counsel/list/${userId}`) }
                )
                return
            } catch (error) {
                console.error(error);
                swalErrorAlert('삭제 실패', '상담내용을 삭제하는 도중에 에러가 발생했습니다.')
                return
            }
        }
        const cancelFunc = () => { return }

        swalConfirm('상담내용 삭제', '상담내용을 삭제하시겠습니까?', 'warning', '삭제', '취소', confirmFunc, cancelFunc)

    }

    return (
        <div className="container-fluid">
            {/* Page Heading */}
            <div className="d-sm-flex align-items-center justify-content-between mb-4">
                <h1 className="h3 mb-0">상담내용 관리</h1>
            </div>

            {/* Content Row */}
            <div className="card shadow mb-4">

                <div className="card-header py-3">
                    <div className='d-flex justify-content-between align-items-center'>
                        <h6 className="m-0 font-weight-bold">
                            {userName}학생 상담내용 {writeType === 'write' ? '작성' : '수정'}
                        </h6>
                        {writeType === 'update' &&
                            <button type='button' className='btn btn-sm btn-danger' onClick={handleDeleteCounselPost}>상담내용 삭제</button>
                        }
                    </div>
                </div>

                <div className="card-body" style={{ height: "calc(100vh - 385px)", overflow: "auto" }}>
                    <div className="">
                        {writeType === 'write' && <CounselWriteForm userId={userId} />}
                        {writeType === 'update' && <CounselWriteForm userId={userId} counselId={counselId} />}
                    </div>
                </div>

                <div className='card-footer'>
                    <div className='text-end'>
                        <Link to={`/hy-admin/counsel/list/${userId}`} className='btn btn-secondary'>목록으로</Link>
                        &nbsp;&nbsp;
                        <button type='submit' form='counselForm' className='btn btn-primary'>저장</button>
                    </div>
                </div>
            </div>
        </div>
    )
}

const CounselWriteForm = ({ userId, counselId }) => {
    const { adminInfo } = useAdminStore(state => state)
    const adminId = adminInfo?.user_id ? adminInfo?.user_id : ''
    const adminUserName = adminInfo?.name ? adminInfo?.name : ''

    const navigate = useNavigate();

    /* React Hook Form */
    const { register, handleSubmit, reset, formState: { errors } } = useForm({ resolver: yupResolver(adminCounselSchema) })


    // 저장되어있던 정보
    const [counselInfo, setCounselInfo] = useState(null)
    const [savedFiles, setSavedFiles] = useState([])
    const [updatedDate, setUpdatedDate] = useState('')

    useEffect(() => {
        getCounselInfo(counselId)
    }, [counselId])

    useEffect(() => {
        reset(counselInfo)
    }, [counselInfo])

    const getCounselInfo = async (counselId) => {
        if (!userId || !counselId) {
            setCounselInfo(null)
            return
        }

        try {
            const response = await adminGetCounselInfoApi(counselId)
            if (response.status === 200) {
                if (response.data?.resData?.writer_id != adminId) {
                    swalAlert('', '올바르지 않은 접근입니다.', 'error', '확인', () => { navigate(-1); })
                    return false;
                }

                const jsonData = response.data?.resData?.json_data ? response.data?.resData?.json_data : null
                const savedFiles = response.data?.resData?.files ? response.data?.resData?.files : []
                const updatedData = response.data?.resData?.updated_date ? response.data?.resData?.updated_date : ''

                setCounselInfo(jsonData)
                setSavedFiles(savedFiles)
                setUpdatedDate(updatedData)
            }
            return

        } catch (error) {
            console.log(error);
            setCounselInfo(null)
            setSavedFiles([])
            setUpdatedDate('')

            swalErrorAlert()
            return
        }
    }

    // 기존 파일 삭제
    const [deleteSavedFile, setDeleteSavedFile] = useState([])
    const onClickDeleteSavedFile = (deleteItemPath) => {
        setSavedFiles((prevState) => { return prevState.filter((item) => item !== deleteItemPath) })
        setDeleteSavedFile((state) => [...state, deleteItemPath])
    }

    // 새로업로드된파일
    const [inputFiles, setInputFiles] = useState([])
    const fileInputRef = useRef(null);
    const handleFileInputChange = (event) => {
        const inputFiles = Array.from(event.target.files);
        setInputFiles((prevFiles) => [...prevFiles, ...inputFiles]);

        event.target.value = "";
    };
    const onClickDeleteFile = (deleteIndex) => {
        setInputFiles((prevFiles) => prevFiles.filter((_, i) => i !== deleteIndex));
    }


    // Submit
    const onSubmit = async (data) => {
        if (!userId || Object.keys(errors).length !== 0) { return false; }

        try {
            // Api Data
            const formData = new FormData();

            formData.append("counselId", JSON.stringify(counselId))

            if (inputFiles && inputFiles.length > 0) {
                inputFiles.forEach((file) => { formData.append("fileData", file) })
            }

            let jsonData = {}
            Object.keys(data).forEach((key) => {
                if (key !== 'files') jsonData[key] = data[key]
            })

            formData.append("jsonData", JSON.stringify(jsonData))

            // 작성자 정보 추가(25.04.09)
            formData.append("writerId", JSON.stringify(adminId))
            formData.append("writerName", JSON.stringify(adminUserName))

            formData.append('deleteSavedFile', JSON.stringify(deleteSavedFile))

            const response = await adminSaveCounselApi(formData, userId)
            if (response.data.code === 201 || response.data.code === 200) {
                reset()
                swalAlert("저장 완료", "성공적으로 저장되었습니다.", "success", "확인", () => window.location.reload(true))
            }
        } catch (error) {
            console.log(error);
            swalErrorAlert('상담내용 작성 에러', '상담내용을 작성하는 도중에 에러가 발생했습니다.')
        }
        finally {
            setInputFiles([])
        }
    }

    return (
        <form id="counselForm" onSubmit={handleSubmit(onSubmit)}>
            <table className='table table-bordered align-middle'>
                <colgroup>
                    <col width={"100px"} />
                    <col width={"auto"} />
                </colgroup>
                <tbody>
                    <tr>
                        <td className='bg-secondary text-light'>제목</td>
                        <td>
                            <input type='text' {...register('title')} className='form-control' placeholder='제목을 입력해주세요.' required />
                        </td>
                    </tr>
                    <tr>
                        <td className='bg-secondary text-light'>상담일</td>
                        <td>
                            <input type='date' {...register('date')} className='form-control w-auto' placeholder='' required />
                            <p className='mt-2 ms-2 small'>우측 달력 아이콘을 누른 후, 날짜를 선택해주세요.</p>
                        </td>
                    </tr>
                    <tr>
                        <td className='bg-secondary text-light'>내용</td>
                        <td>
                            <textarea {...register('content')} className='form-control' placeholder='내용을 입력해주세요.' rows={22} required />
                        </td>
                    </tr>
                    <tr>
                        <td className='bg-secondary text-light'>첨부파일</td>
                        <td>
                            <input
                                type='file'
                                ref={fileInputRef}
                                {...register('files')}
                                className='form-control'
                                onChange={handleFileInputChange}
                                multiple
                            />

                            <div className={savedFiles.length > 0 ? 'mt-3 ms-2' : ''}>
                                <AdminSavedFileList
                                    savedFiles={savedFiles}
                                    onClickDeleteSavedFile={onClickDeleteSavedFile}
                                    fileFolderName={'counsel'}
                                    isEdit={true}
                                />
                            </div>

                            <hr />

                            <div className={inputFiles.length > 0 ? 'mt-2 ms-2' : ''}>
                                <AdminInputFileList
                                    inputFiles={inputFiles}
                                    onClickDeleteFile={onClickDeleteFile}
                                />
                            </div>
                        </td>
                    </tr>
                </tbody>
            </table>
        </form>
    )
}

export default AdminCounselWrite